import FormattedMessage from 'I18n/components/FormattedMessage';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useSelector } from 'react-redux';
import { FREE } from 'self-service-api/constants/ProductLevels';
import styled from 'styled-components';
import { BUNDLE_FEATURE_TABLE_HEADER } from '../../../constants/tracking/Locations';
import { useAppSelector } from '../../../reducers';
import { hasPurchaseMotionsFailedSelector, isMerchCardCTAsDataLoadingSelector } from '../../../selectors/LoadingStateSelectors';
import { calculatedPriceSelector } from '../../../selectors/PricingSelectors';
import { hasProductInProductInventorySelector, quantityPackProductConfigurationsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtils';
import SuiteFeatureTableBadge from '../../common/SuiteFeatureTableBadge';
import CodeSplitEnvWrapper from '../../common/env/CodeSplitEnvWrapper';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import { HighlightedPriceDisplay } from '../../common/pricing/HighlightedPriceDisplay';
import GenericBundleCTAsContainer from '../../merch-card/GenericBundleCTAsContainer';
import FeatureTableFreePriceDisplay from '../FeatureTableFreePriceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TableHeaderCell = styled.th.withConfig({
  displayName: "GenericBundleFeatureTableHeaderCell__TableHeaderCell",
  componentId: "sc-1qqvsjg-0"
})(["text-align:center;font-weight:normal;position:relative;width:", ";padding:16px 0;"], ({
  columns
}) => columns < 3 ? `calc(50%/${columns})` : `calc(70%/${columns})`);
const badgeStyle = {
  top: '16px',
  left: 0,
  right: 0,
  margin: '0 auto',
  width: 'fit-content'
};
const ctaStyles = {
  width: '154px',
  padding: '11px'
};
const GenericBundleFeatureTableHeaderCell = ({
  productLevel,
  columns,
  location,
  screen,
  apiName
}) => {
  const isFree = productLevel === FREE;
  const isCurrentPlan = useAppSelector(state => {
    return hasProductInProductInventorySelector(state, {
      productAttributeFilters: {
        apiName
      }
    });
  });
  const product = useAppSelector(state => {
    return productSelector(state, {
      apiName
    });
  });
  const isMerchCardCTAsDataLoading = useAppSelector(isMerchCardCTAsDataLoadingSelector);
  const hasPurchaseMotionsFailed = useAppSelector(hasPurchaseMotionsFailedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const ownedQuantityPackProductConfigurations = useSelector(state => {
    if (!product) return null;
    const {
      currentSkuId
    } = product;
    return quantityPackProductConfigurationsFromProductInventorySelector(state, {
      baseProductConfigurations: [createProductConfig({
        skuId: currentSkuId
      })]
    });
  }) || [];
  const pricingInfo = useAppSelector(state => {
    if (!product) {
      return null;
    }
    const {
      currentSkuId
    } = product;
    const checkoutProductConfigurations = [createProductConfig({
      skuId: currentSkuId
    }), ...ownedQuantityPackProductConfigurations];
    return calculatedPriceSelector(state, {
      productConfigurations: checkoutProductConfigurations,
      queryParamTerm
    });
  });
  if (!product) return null;
  const {
    currentSkuId
  } = product;
  const contentProps = {
    pricingInfo,
    skuId: currentSkuId,
    concise: true,
    useLargePrice: false
  };
  return /*#__PURE__*/_jsxs(TableHeaderCell, {
    columns: columns,
    children: [isCurrentPlan && /*#__PURE__*/_jsx(SuiteFeatureTableBadge, {
      use: 'koala',
      translationKey: 'currentPlan',
      badgeStyle: badgeStyle
    }), /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `products.${apiName}`
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "m-y-1",
      children: isFree ? /*#__PURE__*/_jsx(FeatureTableFreePriceDisplay, {}) : /*#__PURE__*/_jsx(HighlightedPriceDisplay, Object.assign({}, contentProps))
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: isMerchCardCTAsDataLoading,
      isError: hasPurchaseMotionsFailed,
      heights: [40],
      widths: [140],
      className: "justify-center",
      children: isFree ? /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
        authedComponentLoader: () => import( /* webpackChunkName: "authed-chunk" */`pricing-pages-ui/upgrade-page/CurrentPlanButton`),
        publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/common/SignupButton`),
        authedComponentProps: {
          ctaStyles: Object.assign({}, ctaStyles, {
            visibility: `${isCurrentPlan ? 'visible' : 'hidden'}`
          })
        },
        publicComponentProps: {
          style: ctaStyles,
          sourceId: BUNDLE_FEATURE_TABLE_HEADER,
          use: 'primary'
        }
      }) : /*#__PURE__*/_jsx(GenericBundleCTAsContainer, {
        screen: screen,
        apiName: apiName,
        trackingSource: location,
        returnFirstCTAOnly: true
      })
    })]
  });
};
export default GenericBundleFeatureTableHeaderCell;